// Folder access for the TeslaCam navigator: the File System Access picker where the browser has it,
// otherwise callers fall back to <input webkitdirectory>.

interface FileHandleLike {
  kind: 'file'
  name: string
  getFile(): Promise<File>
}

interface DirectoryHandleLike {
  kind: 'directory'
  name: string
  values(): AsyncIterable<FileHandleLike | DirectoryHandleLike>
}

export type DirectoryHandle = DirectoryHandleLike

type PickerWindow = Window & {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<DirectoryHandleLike>
}

export function supportsDirectoryPicker(): boolean {
  return typeof (window as PickerWindow).showDirectoryPicker === 'function'
}

/** Opens the read-only directory picker. Rejects with an AbortError if the user cancels. */
export function pickDirectory(): Promise<DirectoryHandle> {
  const win = window as PickerWindow
  if (!win.showDirectoryPicker) throw new Error('showDirectoryPicker is not supported')
  return win.showDirectoryPicker({ mode: 'read' })
}

// webkitRelativePath is read-only on File, so it is defined here, once, to match what
// <input webkitdirectory> produces and what the Tesla parser reads.
function withRelativePath(file: File, path: string): File {
  Object.defineProperty(file, 'webkitRelativePath', { value: path, configurable: true })
  return file
}

/** Recursively lists every file under the handle, with webkitRelativePath set to `<root>/<path>`. */
export async function filesFromDirectoryHandle(handle: DirectoryHandle): Promise<File[]> {
  const files: File[] = []

  async function walk(dir: DirectoryHandleLike, prefix: string): Promise<void> {
    for await (const entry of dir.values()) {
      const path = `${prefix}/${entry.name}`
      if (entry.kind === 'directory') {
        await walk(entry, path)
        continue
      }
      files.push(withRelativePath(await entry.getFile(), path))
    }
  }

  await walk(handle, handle.name)
  return files
}
